import React, { useState, useEffect, useRef } from "react";

const API_URL = "https://danielcantos.net/api";
const get = (url) => fetch(API_URL + url).then(r => r.json());
const post = (url, data) => fetch(API_URL + url, {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(data)}).then(r => r.json());
const put = (url, data) => fetch(API_URL + url, {method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(data)}).then(r => r.json());
const del = (url) => fetch(API_URL + url, {method:"DELETE"});

const COLORS = ["#1E3A5F","#2C6E7F","#4A7C8C","#5B8A9C","#3D5A80","#2E5266","#6B8CAE","#476072","#375169","#4E6E8E"];
const PRIORITIES = [{val:"high",label:"Alta",color:"#EF4444",bg:"#FEE2E2"},{val:"medium",label:"Media",color:"#F59E0B",bg:"#FEF9C3"},{val:"low",label:"Baja",color:"#10B981",bg:"#DCFCE7"}];

const GOLD = "#D4AF37";
const NAVY = "#0d1b3e";
const SAVED_RECIPIENTS_KEY = "gantt_saved_recipients";

function diffDays(a, b) { return Math.round((new Date(b) - new Date(a)) / 86400000); }
function addDays(date, days) { const d = new Date(date); d.setDate(d.getDate() + days); return d.toISOString().split("T")[0]; }
function formatDate(d) { if (!d) return ""; return new Date(d).toLocaleDateString("es-ES", {day:"2-digit",month:"short",year:"numeric"}); }

export default function GanttChart({ projectId, projectName, projectStart, projectEnd, currency }) {
  const [tasks, setTasks] = useState([]);
  const [taskOrder, setTaskOrder] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [zoom, setZoom] = useState("week");
  const [form, setForm] = useState({name:"",description:"",start_date:"",end_date:"",duration_days:1,priority:"medium",assigned_to:"",color:"#3B82F6",progress:0});
  const ganttRef = useRef(null);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [savedRecipients, setSavedRecipients] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  // Cargar destinatarios guardados al montar
  useEffect(() => {
    const saved = localStorage.getItem(SAVED_RECIPIENTS_KEY);
    if (saved) { setSavedRecipients(saved); setEmailTo(saved); }
  }, []);

  useEffect(() => { loadTasks(); }, [projectId]);

  const loadTasks = async () => {
    const r = await get("/projects/" + projectId + "/tasks");
    const loaded = Array.isArray(r) ? r : [];
    setTasks(loaded);
    // Cargar orden guardado
    const savedOrder = localStorage.getItem(`gantt_order_${projectId}`);
    if (savedOrder) {
      const orderIds = JSON.parse(savedOrder);
      // Ordenar según IDs guardados, nuevas tareas al final
      const ordered = [
        ...orderIds.map(id => loaded.find(t => t.id === id)).filter(Boolean),
        ...loaded.filter(t => !orderIds.includes(t.id))
      ];
      setTaskOrder(ordered);
    } else {
      setTaskOrder(loaded);
    }
  };

  const saveOrder = (ordered) => {
    localStorage.setItem(`gantt_order_${projectId}`, JSON.stringify(ordered.map(t => t.id)));
    setTaskOrder(ordered);
  };

  const moveTask = (idx, dir) => {
    const newOrder = [...taskOrder];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    saveOrder(newOrder);
  };

  const handleStartDate = (val) => {
    const end = form.end_date || addDays(val, form.duration_days || 1);
    const days = diffDays(val, end);
    setForm({...form, start_date: val, end_date: end, duration_days: Math.max(1, days)});
  };
  const handleEndDate = (val) => {
    const days = diffDays(form.start_date, val);
    setForm({...form, end_date: val, duration_days: Math.max(1, days)});
  };
  const handleDuration = (val) => {
    const days = parseInt(val) || 1;
    const end = form.start_date ? addDays(form.start_date, days) : "";
    setForm({...form, duration_days: days, end_date: end});
  };

  const saveTask = async () => {
    if (!form.name || !form.start_date || !form.end_date) return alert("Completa nombre, inicio y fin");
    const data = {...form, start_date: form.start_date+"T00:00:00", end_date: form.end_date+"T00:00:00", dependencies: []};
    if (editTask) {
      await put("/projects/"+projectId+"/tasks/"+editTask.id+"/progress?progress="+form.progress, {});
      await del("/projects/"+projectId+"/tasks/"+editTask.id);
      await post("/projects/"+projectId+"/tasks", data);
    } else {
      await post("/projects/"+projectId+"/tasks", data);
    }
    setShowForm(false); setEditTask(null);
    setForm({name:"",description:"",start_date:"",end_date:"",duration_days:1,priority:"medium",assigned_to:"",color:"#3B82F6",progress:0});
    loadTasks();
  };

  const deleteTask = async (id) => {
    if (window.confirm("¿Eliminar esta tarea?")) {
      await del("/projects/"+projectId+"/tasks/"+id);
      // Limpiar del orden guardado
      const newOrder = taskOrder.filter(t => t.id !== id);
      saveOrder(newOrder);
      loadTasks();
    }
  };

  const updateProgress = async (task, progress) => {
    await fetch(API_URL+"/projects/"+projectId+"/tasks/"+task.id+"/progress?progress="+progress, {method:"PUT"});
    loadTasks();
  };

  const openEdit = (task) => {
    setEditTask(task);
    setForm({
      name: task.name, description: task.description||"",
      start_date: task.start_date?.split("T")[0]||"",
      end_date: task.end_date?.split("T")[0]||"",
      duration_days: task.duration_days||1, priority: task.priority||"medium",
      assigned_to: task.assigned_to||"", color: task.color||"#3B82F6", progress: task.progress||0
    });
    setShowForm(true);
  };

  // ── EMAIL FUNCTIONS ──
  const saveRecipients = () => {
    if (!emailTo.trim()) return;
    localStorage.setItem(SAVED_RECIPIENTS_KEY, emailTo.trim());
    setSavedRecipients(emailTo.trim());
    setSaveMsg("✅ Guardado");
    setTimeout(() => setSaveMsg(""), 2000);
  };

  const generateEmailDraft = async () => {
    if (!emailTo.trim()) return alert("Ingresa al menos un destinatario");
    setEmailLoading(true);
    setEmailDraft("");
    setEmailSent(false);
    setEmailEditMode(false);
    try {
      const completedTasks = tasks.filter(t => t.progress >= 100).map(t => t.name);
      const pendingTasks = tasks.filter(t => t.progress < 100).map(t => `${t.name} (${t.progress||0}%)`);
      const r = await post("/email/draft-gantt", {
        project_name: projectName,
        progress: avgProgress,
        completed_tasks: completedTasks,
        pending_tasks: pendingTasks,
        to: emailTo.split(",").map(e => e.trim()).filter(Boolean)
      });
      setEmailDraft(r.draft);
      setEmailHtml(r.html_email);
      setEmailSubject(r.subject);
    } catch(e) {
      alert("Error generando el correo: " + e.message);
    }
    setEmailLoading(false);
  };

  const sendEmail = async () => {
    setEmailSending(true);
    try {
      await post("/email/send", {
        to: emailTo.split(",").map(e => e.trim()).filter(Boolean),
        subject: emailSubject,
        body: emailHtml
      });
      setEmailSent(true);
    } catch(e) {
      alert("Error enviando: " + e.message);
    }
    setEmailSending(false);
  };

  // Gantt timeline
  const allStart = tasks.length ? tasks.reduce((a,t) => t.start_date < a ? t.start_date : a, tasks[0].start_date) : projectStart||new Date().toISOString();
  const allEnd = tasks.length ? tasks.reduce((a,t) => t.end_date > a ? t.end_date : a, tasks[0].end_date) : addDays(allStart, 30);
  const timelineStart = new Date(allStart);
  const timelineEnd = new Date(allEnd);
  timelineStart.setDate(timelineStart.getDate() - 2);
  timelineEnd.setDate(timelineEnd.getDate() + 5);

  const totalDays = diffDays(timelineStart, timelineEnd);
  const dayW = zoom === "day" ? 40 : zoom === "week" ? 20 : 8;
  const totalW = totalDays * dayW;
  const getX = (date) => diffDays(timelineStart, new Date(date)) * dayW;
  const getW = (start, end) => Math.max(diffDays(new Date(start), new Date(end)) * dayW, dayW);
  const today = new Date();
  const todayX = getX(today);

  const markers = [];
  let cur = new Date(timelineStart);
  while (cur <= timelineEnd) {
    markers.push(new Date(cur));
    if (zoom === "day") cur.setDate(cur.getDate() + 1);
    else if (zoom === "week") cur.setDate(cur.getDate() + 7);
    else cur.setMonth(cur.getMonth() + 1);
  }

  const avgProgress = tasks.length ? Math.round(tasks.reduce((a,t) => a + (t.progress||0), 0) / tasks.length) : 0;
  const completed = tasks.filter(t => t.progress >= 100).length;
  const inProgress = tasks.filter(t => t.progress > 0 && t.progress < 100).length;

  const card = {background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1px solid #E2E8F0"};
  const inp = {border:"1px solid #CBD5E1",borderRadius:8,padding:"8px 12px",fontSize:13,width:"100%"};
  const btn = {background:"#2563EB",color:"white",border:"none",borderRadius:8,padding:"10px 18px",cursor:"pointer",fontWeight:600,fontSize:13};

  return (
    <div style={{fontFamily:"Inter,sans-serif"}}>

      {/* ── EMAIL MODAL ── */}
      {showEmailModal && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}}>
          <div style={{background:NAVY,border:`1px solid rgba(212,175,55,0.3)`,borderRadius:16,padding:32,width:"min(640px,95vw)",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,0.6)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{color:GOLD,fontSize:16,fontWeight:700}}>✉️ Notificación de Avance</div>
                <div style={{color:"rgba(255,255,255,0.4)",fontSize:12,marginTop:2}}>{projectName} — {avgProgress}% completado</div>
              </div>
              <button onClick={()=>{setShowEmailModal(false);setEmailDraft("");setEmailSent(false);setEmailEditMode(false);}} style={{background:"rgba(255,255,255,0.08)",border:"none",color:"rgba(255,255,255,0.6)",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:13}}>✕ Cerrar</button>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <label style={{fontSize:11,color:GOLD,letterSpacing:"1px",textTransform:"uppercase"}}>Destinatarios</label>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {saveMsg && <span style={{fontSize:11,color:"#2ecc71",fontWeight:600}}>{saveMsg}</span>}
                  {savedRecipients && savedRecipients !== emailTo && (
                    <button onClick={()=>setEmailTo(savedRecipients)} style={{fontSize:11,padding:"3px 8px",borderRadius:5,background:"rgba(212,175,55,0.1)",color:GOLD,border:`1px solid rgba(212,175,55,0.25)`,cursor:"pointer",fontWeight:600}}>↩ Restaurar</button>
                  )}
                  <button onClick={saveRecipients} style={{fontSize:11,padding:"3px 10px",borderRadius:5,background:"rgba(212,175,55,0.15)",color:GOLD,border:`1px solid rgba(212,175,55,0.3)`,cursor:"pointer",fontWeight:700}}>💾 Guardar</button>
                </div>
              </div>
              <input style={{...inp,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(212,175,55,0.3)",color:"white",borderRadius:8,padding:"10px 14px"}} placeholder="correo1@ejemplo.com, correo2@ejemplo.com" value={emailTo} onChange={e=>setEmailTo(e.target.value)}/>
              {savedRecipients && <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:4}}>💾 {savedRecipients}</div>}
            </div>
            <div style={{background:"rgba(255,255,255,0.05)",borderRadius:8,padding:"12px 16px",marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:12,color:"rgba(255,255,255,0.5)"}}>Progreso</span>
                <span style={{fontSize:14,fontWeight:700,color:GOLD}}>{avgProgress}%</span>
              </div>
              <div style={{background:"rgba(255,255,255,0.1)",borderRadius:20,height:8}}>
                <div style={{background:GOLD,height:8,borderRadius:20,width:avgProgress+"%",transition:"width 0.3s"}}/>
              </div>
              <div style={{display:"flex",gap:16,marginTop:8,fontSize:11,color:"rgba(255,255,255,0.4)"}}>
                <span>✅ {completed} completadas</span><span>⚡ {inProgress} en progreso</span><span>📋 {tasks.length} total</span>
              </div>
            </div>
            {!emailDraft && (
              <button onClick={generateEmailDraft} disabled={emailLoading} style={{width:"100%",padding:"12px",background:GOLD,color:NAVY,border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:14,opacity:emailLoading?0.7:1,marginBottom:16}}>
                {emailLoading ? "🤖 Redactando..." : "🤖 Generar correo con IA"}
              </button>
            )}
            {emailDraft && !emailSent && (
              <div>
                <div style={{marginBottom:12}}>
                  <label style={{fontSize:11,color:GOLD,letterSpacing:"1px",textTransform:"uppercase",display:"block",marginBottom:6}}>Asunto</label>
                  <input style={{...inp,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(212,175,55,0.2)",color:"white",borderRadius:8,padding:"8px 12px"}} value={emailSubject} onChange={e=>setEmailSubject(e.target.value)}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <label style={{fontSize:11,color:GOLD,letterSpacing:"1px",textTransform:"uppercase"}}>{emailEditMode ? "✏️ Editando" : "👁 Preview"}</label>
                  <button onClick={()=>setEmailEditMode(!emailEditMode)} style={{fontSize:11,padding:"4px 12px",borderRadius:6,fontWeight:700,cursor:"pointer",background:emailEditMode?"rgba(212,175,55,0.15)":"rgba(255,255,255,0.08)",color:emailEditMode?GOLD:"rgba(255,255,255,0.6)",border:emailEditMode?`1px solid rgba(212,175,55,0.3)`:"1px solid rgba(255,255,255,0.1)"}}>
                    {emailEditMode ? "👁 Ver preview" : "✏️ Editar texto"}
                  </button>
                </div>
                <div style={{marginBottom:16}}>
                  {emailEditMode ? (
                    <textarea value={emailDraft} onChange={e=>{setEmailDraft(e.target.value);setEmailHtml(e.target.value.replace(/\n/g,"<br/>"));}} style={{width:"100%",minHeight:200,background:"rgba(255,255,255,0.04)",border:`1px solid rgba(212,175,55,0.25)`,borderRadius:8,padding:"12px 14px",fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.6,resize:"vertical",outline:"none",boxSizing:"border-box",fontFamily:"Inter,sans-serif"}}/>
                  ) : (
                    <div style={{background:"white",borderRadius:8,padding:16,maxHeight:300,overflowY:"auto"}} dangerouslySetInnerHTML={{__html:emailHtml}}/>
                  )}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={sendEmail} disabled={emailSending} style={{flex:1,padding:"12px",background:GOLD,color:NAVY,border:"none",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:14,opacity:emailSending?0.7:1}}>
                    {emailSending ? "Enviando..." : "📤 Confirmar y Enviar"}
                  </button>
                  <button onClick={()=>{setEmailDraft("");setEmailSent(false);setEmailEditMode(false);}} style={{padding:"12px 20px",background:"rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.6)",border:"none",borderRadius:8,cursor:"pointer",fontWeight:600}}>Regenerar</button>
                </div>
              </div>
            )}
            {emailSent && (
              <div style={{textAlign:"center",padding:"24px 0"}}>
                <div style={{fontSize:48,marginBottom:12}}>✅</div>
                <div style={{color:GOLD,fontSize:18,fontWeight:700,marginBottom:8}}>¡Correo enviado!</div>
                <div style={{color:"rgba(255,255,255,0.5)",fontSize:13}}>El avance fue notificado exitosamente.</div>
                <button onClick={()=>{setShowEmailModal(false);setEmailDraft("");setEmailSent(false);setEmailEditMode(false);}} style={{marginTop:20,padding:"10px 24px",background:GOLD,color:NAVY,border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>Cerrar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[["Total tareas",tasks.length,"#3B82F6","📋"],["Completadas",completed,"#10B981","✅"],["En progreso",inProgress,"#F59E0B","⚡"],["Progreso avg.",avgProgress+"%","#8B5CF6","📊"]].map(([l,v,c,i]) => (
          <div key={l} style={{...card,padding:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div><div style={{fontSize:12,color:"#64748B",marginBottom:4}}>{l}</div><div style={{fontSize:24,fontWeight:800,color:c}}>{v}</div></div>
              <span style={{fontSize:28}}>{i}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:600,color:"#64748B"}}>Zoom:</span>
          {["day","week","month"].map(z => (
            <button key={z} onClick={()=>setZoom(z)} style={{background:zoom===z?"#2563EB":"#F1F5F9",color:zoom===z?"white":"#475569",border:"none",borderRadius:6,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:600}}>
              {z==="day"?"Día":z==="week"?"Semana":"Mes"}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          {tasks.length > 0 && (
            <button onClick={()=>setShowEmailModal(true)} style={{background:NAVY,color:GOLD,border:`1px solid rgba(212,175,55,0.4)`,borderRadius:8,padding:"10px 16px",cursor:"pointer",fontWeight:600,fontSize:13,display:"flex",alignItems:"center",gap:6}}>
              ✉️ Notificar avance
            </button>
          )}
          <button onClick={()=>{setShowForm(true);setEditTask(null);setForm({name:"",description:"",start_date:"",end_date:"",duration_days:1,priority:"medium",assigned_to:"",color:COLORS[tasks.length%COLORS.length],progress:0});}} style={btn}>
            + Nueva Tarea
          </button>
        </div>
      </div>

      {/* Task Form */}
      {showForm && (
        <div style={{...card,marginBottom:20,border:"2px solid #2563EB"}}>
          <h3 style={{fontWeight:700,marginBottom:16,color:"#1E293B"}}>{editTask?"Editar Tarea":"Nueva Tarea"}</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>NOMBRE *</label><input style={inp} placeholder="Nombre de la tarea" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>RESPONSABLE</label><input style={inp} placeholder="Asignado a..." value={form.assigned_to} onChange={e=>setForm({...form,assigned_to:e.target.value})}/></div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>PRIORIDAD</label>
              <select style={inp} value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                {PRIORITIES.map(p=><option key={p.val} value={p.val}>{p.label}</option>)}
              </select>
            </div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>FECHA INICIO *</label><input type="date" style={inp} value={form.start_date} onChange={e=>handleStartDate(e.target.value)}/></div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>FECHA FIN *</label><input type="date" style={inp} value={form.end_date} onChange={e=>handleEndDate(e.target.value)}/></div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>DURACIÓN (días)</label>
              <input type="number" style={{...inp,background:"#F8FAFC",fontWeight:700,color:"#2563EB"}} value={form.duration_days} onChange={e=>handleDuration(e.target.value)} min="1"/>
            </div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>DESCRIPCIÓN</label><input style={inp} placeholder="Descripción opcional" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>PROGRESO: {form.progress}%</label>
              <input type="range" min="0" max="100" value={form.progress} onChange={e=>setForm({...form,progress:Number(e.target.value)})} style={{width:"100%",marginTop:8}}/>
            </div>
            <div><label style={{fontSize:11,color:"#64748B",fontWeight:600}}>COLOR</label>
              <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
                {COLORS.map(c=><div key={c} onClick={()=>setForm({...form,color:c})} style={{width:24,height:24,borderRadius:"50%",background:c,cursor:"pointer",border:form.color===c?"3px solid #1E293B":"2px solid transparent"}}/>)}
              </div>
            </div>
          </div>
          {form.start_date && form.end_date && (
            <div style={{marginTop:12,padding:"8px 12px",background:"#EFF6FF",borderRadius:8,fontSize:13,color:"#2563EB",fontWeight:600}}>
              📅 {formatDate(form.start_date)} → {formatDate(form.end_date)} — <strong>{form.duration_days} días</strong>
            </div>
          )}
          <div style={{display:"flex",gap:8,marginTop:16}}>
            <button onClick={saveTask} style={btn}>{editTask?"Guardar cambios":"Agregar tarea"}</button>
            <button onClick={()=>{setShowForm(false);setEditTask(null);}} style={{background:"#F1F5F9",color:"#475569",border:"none",borderRadius:8,padding:"10px 18px",cursor:"pointer",fontWeight:600}}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Gantt Chart */}
      {taskOrder.length > 0 && (
        <div style={card}>
          <div style={{overflowX:"auto"}} ref={ganttRef}>
            <div style={{minWidth: 340 + totalW}}>
              <div style={{display:"flex",borderBottom:"2px solid #E2E8F0",paddingBottom:8,marginBottom:0}}>
                <div style={{width:40,minWidth:40}}/>
                <div style={{width:300,minWidth:300,fontWeight:700,fontSize:12,color:"#64748B",padding:"0 12px"}}>TAREA</div>
                <div style={{position:"relative",width:totalW,height:40}}>
                  {markers.map((m,i) => (
                    <div key={i} style={{position:"absolute",left:getX(m),fontSize:11,color:"#94A3B8",fontWeight:600,whiteSpace:"nowrap",borderLeft:"1px solid #E2E8F0",paddingLeft:4,height:"100%"}}>
                      {zoom==="day" ? m.toLocaleDateString("es-ES",{day:"2-digit",month:"short"}) :
                       zoom==="week" ? m.toLocaleDateString("es-ES",{day:"2-digit",month:"short"}) :
                       m.toLocaleDateString("es-ES",{month:"long",year:"numeric"})}
                    </div>
                  ))}
                  {todayX >= 0 && todayX <= totalW && (
                    <div style={{position:"absolute",left:todayX,top:0,height:"100%",display:"flex",alignItems:"center"}}>
                      <span style={{background:"#EF4444",color:"white",fontSize:9,fontWeight:700,padding:"2px 4px",borderRadius:4,whiteSpace:"nowrap"}}>HOY</span>
                    </div>
                  )}
                </div>
              </div>
              {taskOrder.map((task, idx) => {
                const tx = getX(task.start_date);
                const tw = getW(task.start_date, task.end_date);
                const pri = PRIORITIES.find(p=>p.val===task.priority)||PRIORITIES[1];
                const dur = diffDays(new Date(task.start_date), new Date(task.end_date));
                return (
                  <div key={task.id} style={{display:"flex",alignItems:"center",borderBottom:"1px solid #F1F5F9",minHeight:56}}>
                    {/* Botones orden */}
                    <div style={{width:40,minWidth:40,display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"0 4px"}}>
                      <button
                        onClick={()=>moveTask(idx,-1)}
                        disabled={idx===0}
                        title="Subir"
                        style={{background:idx===0?"#F8FAFC":"#EFF6FF",color:idx===0?"#CBD5E1":"#2563EB",border:"none",borderRadius:4,width:24,height:20,cursor:idx===0?"default":"pointer",fontSize:11,fontWeight:700,lineHeight:1}}>
                        ▲
                      </button>
                      <button
                        onClick={()=>moveTask(idx,1)}
                        disabled={idx===taskOrder.length-1}
                        title="Bajar"
                        style={{background:idx===taskOrder.length-1?"#F8FAFC":"#EFF6FF",color:idx===taskOrder.length-1?"#CBD5E1":"#2563EB",border:"none",borderRadius:4,width:24,height:20,cursor:idx===taskOrder.length-1?"default":"pointer",fontSize:11,fontWeight:700,lineHeight:1}}>
                        ▼
                      </button>
                    </div>
                    <div style={{width:300,minWidth:300,padding:"8px 12px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:10,height:10,borderRadius:"50%",background:task.color||"#3B82F6",flexShrink:0}}/>
                        <div style={{fontWeight:600,fontSize:13,color:"#1E293B",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{task.name}</div>
                        <span style={{background:pri.bg,color:pri.color,borderRadius:20,padding:"1px 6px",fontSize:10,fontWeight:700,flexShrink:0}}>{pri.label}</span>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                        <div style={{fontSize:11,color:"#94A3B8"}}>{task.assigned_to||"Sin asignar"} · {dur}d</div>
                        <div style={{flex:1,background:"#F1F5F9",borderRadius:20,height:4}}>
                          <div style={{background:task.color||"#3B82F6",height:4,borderRadius:20,width:(task.progress||0)+"%",transition:"width 0.3s"}}/>
                        </div>
                        <span style={{fontSize:11,fontWeight:700,color:"#2563EB"}}>{task.progress||0}%</span>
                      </div>
                    </div>
                    <div style={{position:"relative",width:totalW,height:56,overflow:"visible"}}>
                      {todayX >= 0 && todayX <= totalW && (
                        <div style={{position:"absolute",left:todayX,top:0,width:2,height:"100%",background:"#EF4444",opacity:0.5,zIndex:1}}/>
                      )}
                      <div onClick={() => openEdit(task)} style={{position:"absolute",left:tx,width:tw,top:"50%",transform:"translateY(-50%)",height:32,background:task.color||"#3B82F6",borderRadius:6,cursor:"pointer",boxShadow:"0 2px 4px rgba(0,0,0,0.15)",overflow:"hidden",zIndex:2,transition:"all 0.2s"}} title={`${task.name}: ${formatDate(task.start_date)} → ${formatDate(task.end_date)}`}>
                        <div style={{position:"absolute",left:0,top:0,height:"100%",width:(task.progress||0)+"%",background:"rgba(255,255,255,0.3)",borderRadius:6,transition:"width 0.3s"}}/>
                        <span style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"white",fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",maxWidth:tw-16}}>{task.name}</span>
                        {tw > 60 && <span style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",fontSize:10,color:"rgba(255,255,255,0.9)",fontWeight:600}}>{task.progress||0}%</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:4,padding:"0 8px",flexShrink:0}}>
                      <button onClick={()=>updateProgress(task, Math.min(100,(task.progress||0)+25))} title="Avanzar 25%" style={{background:"#DCFCE7",color:"#16A34A",border:"none",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12,fontWeight:700}}>+25%</button>
                      <button onClick={()=>openEdit(task)} style={{background:"#EFF6FF",color:"#2563EB",border:"none",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>✏️</button>
                      <button onClick={()=>deleteTask(task.id)} style={{background:"#FEE2E2",color:"#DC2626",border:"none",borderRadius:6,padding:"4px 8px",cursor:"pointer",fontSize:12}}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{marginTop:16,paddingTop:12,borderTop:"1px solid #F1F5F9",display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#64748B"}}><div style={{width:12,height:12,background:"#EF4444",borderRadius:2}}/> Línea de hoy</div>
            <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#64748B"}}><div style={{width:40,height:8,background:"#3B82F6",borderRadius:4,position:"relative",overflow:"hidden"}}><div style={{position:"absolute",left:0,width:"60%",height:"100%",background:"rgba(255,255,255,0.4)"}}/></div> Progreso</div>
            <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#64748B"}}>▲▼ Reordenar filas</div>
            <div style={{marginLeft:"auto",fontSize:12,color:"#64748B"}}>Click en barra para editar · +25% para avanzar rápido</div>
          </div>
        </div>
      )}

      {tasks.length === 0 && !showForm && (
        <div style={{...card,textAlign:"center",padding:60}}>
          <div style={{fontSize:48,marginBottom:16}}>📊</div>
          <div style={{fontSize:16,fontWeight:700,color:"#1E293B",marginBottom:8}}>Sin tareas aún</div>
          <div style={{fontSize:13,color:"#94A3B8",marginBottom:20}}>Agrega tu primera tarea para ver el diagrama Gantt</div>
          <button onClick={()=>setShowForm(true)} style={btn}>+ Agregar primera tarea</button>
        </div>
      )}
    </div>
  );
}
